// Preview + confirm dialog for booking ONE PaySimple payment into an order from its mapped subscription
// (schedule). Mirrors the old reconciliation page's inline dialog. Creation is business data — the
// preview + explicit button is the go; back-dated, no email.
import React, { useState } from 'react';
import {
  Box, Typography, Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { post } from '../../../utils/api';
import { fmtMoney, fmtDate } from '../reconShared';

export default function PaysimpleCreateOrderDialog({ row, mapping, onClose, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  if (!row) return null;
  const m = mapping || {};
  const splits = Array.isArray(m.splits) ? m.splits : [];
  const prodSum = Math.round(splits.reduce((a, s) => a + Number(s.amount || 0), 0) * 100) / 100;
  const charge = Number(row.amount_num || 0);
  const fee = Math.round((charge - prodSum) * 100) / 100;
  const over = fee < -0.005;
  const doCreate = async () => {
    setCreating(true); setErr('');
    try { const res = await post('/api/manage/paysimple-reconcile/create-one', { payment_id: row.payment_id }); onCreated(res); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Could not create the order'); }
    finally { setCreating(false); }
  };
  return (
    <Dialog open onClose={() => !creating && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>Create order from subscription<IconButton onClick={onClose} disabled={creating} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Devotee</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.customer_name || '(mapped devotee)'}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Order date</Typography><Typography variant="body2">{fmtDate(row.created_at)}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Subscription</Typography><Typography variant="body2">{row.schedule_id}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Payment</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.payment_id}</Typography></Grid>
        </Grid>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead><TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}><TableCell>Product</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
          <TableBody>
            {splits.map((s, i) => (<TableRow key={i}><TableCell>{s.label}</TableCell><TableCell align="right">{fmtMoney(s.amount)}</TableCell></TableRow>))}
            {fee > 0.005 && (<TableRow><TableCell sx={{ color: 'text.secondary' }}>Processing fee</TableCell><TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtMoney(fee)}</TableCell></TableRow>)}
            <TableRow><TableCell sx={{ fontWeight: 700, borderBottom: 0 }}>Total (charged)</TableCell><TableCell align="right" sx={{ fontWeight: 700, borderBottom: 0 }}>{fmtMoney(charge)}</TableCell></TableRow>
          </TableBody>
        </Table>
        {over && <Alert severity="warning" sx={{ mt: 1.5 }}>Allocation ({fmtMoney(prodSum)}) exceeds the charge ({fmtMoney(charge)}). Fix the mapping first.</Alert>}
        {err && <Alert severity="error" sx={{ mt: 1.5 }}>{err}</Alert>}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>Books like prior months for this subscription — back-dated to {fmtDate(row.created_at)}, no email sent.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>Cancel</Button>
        <Button variant="contained" onClick={doCreate} disabled={creating || over} startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}>Create order</Button>
      </DialogActions>
    </Dialog>
  );
}
